import * as d3 from 'd3';
import { Block, Line } from '../../types';
import { STYLES } from '../../styles/constants';

/** Part du tracé depuis la sortie du losange (complété par une marge px pour ne pas masquer le sommet). */
const DECISION_EXIT_LABEL_FRACTION = 0.22;
/** Distance mini le long de la ligne depuis l’ancre — évite que « Oui » chevauche le diamant. */
const DECISION_EXIT_MIN_CLEARANCE_PX = 22;
const MAX_DECISION_EXIT_WORDS = 2;

function clampDecisionExitLabel(text: string): string {
  const w = text.trim().split(/\s+/).filter(Boolean);
  return w.slice(0, MAX_DECISION_EXIT_WORDS).join(' ');
}

/** Affichage sortie losange : `exitLabel`, ou rétrocompat anciennes cartes (`text`). */
function getDecisionExitDisplayText(line: Line): string {
  const ex = (line.exitLabel ?? '').trim();
  if (ex) return ex;
  return (line.text ?? '').trim();
}

export class LineTextManager {
  private container: d3.Selection<HTMLDivElement, unknown, null, undefined>;
  private editMode: boolean;
  private onUpdate: (line: Line) => void;

  constructor(
    container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
    editMode: { enabled: boolean },
    onUpdate: (line: Line) => void
  ) {
    this.container = container;
    this.editMode = editMode.enabled;
    this.onUpdate = onUpdate;
  }

  /**
   * Trouve le segment actuel où se trouve le label et sa direction
   * @param points Les points de la ligne
   * @param offsetDistance Distance depuis le point médian
   * @returns Informations sur le segment actuel: {segmentIndex, isHorizontal, isVertical, unitX, unitY}
   */
  private findCurrentSegment(
    points: { x: number; y: number }[],
    offsetDistance: number
  ): { segmentIndex: number; isHorizontal: boolean; isVertical: boolean; unitX: number; unitY: number } | null {
    if (points.length < 2) return null;

    // Calculer la longueur totale de la ligne
    let totalLength = 0;
    const segmentLengths: number[] = [];
    
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      segmentLengths.push(length);
      totalLength += length;
    }

    // Trouver le point médian
    const midLength = totalLength / 2;
    let targetLength = midLength + offsetDistance;
    targetLength = Math.max(0, Math.min(totalLength, targetLength));

    // Trouver le segment actuel
    let currentLength = 0;
    for (let i = 0; i < segmentLengths.length; i++) {
      if (currentLength + segmentLengths[i] >= targetLength) {
        const segmentStart = points[i];
        const segmentEnd = points[i + 1];
        const segmentDx = segmentEnd.x - segmentStart.x;
        const segmentDy = segmentEnd.y - segmentStart.y;
        const segmentLength = segmentLengths[i];
        
        if (segmentLength < 0.1) {
          return null;
        }
        
        const unitX = segmentDx / segmentLength;
        const unitY = segmentDy / segmentLength;
        
        // Déterminer si le segment est horizontal, vertical ou diagonal
        const isHorizontal = Math.abs(segmentDy) < Math.abs(segmentDx) * 0.1;
        const isVertical = Math.abs(segmentDx) < Math.abs(segmentDy) * 0.1;
        
        return {
          segmentIndex: i,
          isHorizontal,
          isVertical,
          unitX,
          unitY
        };
      }
      currentLength += segmentLengths[i];
    }

    return null;
  }

  /**
   * Trouve un point le long de la ligne (polyline) à une distance donnée depuis le point médian
   * @param points Les points de la ligne
   * @param offsetDistance Distance depuis le point médian (positif = vers la fin, négatif = vers le début)
   * @returns Position {x, y} le long de la ligne
   */
  private findPointAlongLine(
    points: { x: number; y: number }[],
    offsetDistance: number
  ): { x: number; y: number } {
    if (points.length < 2) {
      return points[0] || { x: 0, y: 0 };
    }

    // Calculer la longueur totale de la ligne
    let totalLength = 0;
    const segmentLengths: number[] = [];
    
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      segmentLengths.push(length);
      totalLength += length;
    }

    // Trouver le point médian (milieu de la ligne)
    const midLength = totalLength / 2;
    let currentLength = 0;
    let midSegmentIndex = 0;
    let midSegmentOffset = 0;

    for (let i = 0; i < segmentLengths.length; i++) {
      if (currentLength + segmentLengths[i] >= midLength) {
        midSegmentIndex = i;
        midSegmentOffset = midLength - currentLength;
        break;
      }
      currentLength += segmentLengths[i];
    }

    // Point médian de référence
    const midSegmentStart = points[midSegmentIndex];
    const midSegmentEnd = points[midSegmentIndex + 1];
    const midSegmentDx = midSegmentEnd.x - midSegmentStart.x;
    const midSegmentDy = midSegmentEnd.y - midSegmentStart.y;
    const midSegmentLength = segmentLengths[midSegmentIndex];
    
    const midPoint = {
      x: midSegmentStart.x + (midSegmentDx * (midSegmentOffset / midSegmentLength)),
      y: midSegmentStart.y + (midSegmentDy * (midSegmentOffset / midSegmentLength))
    };

    // Si pas d'offset, retourner le point médian
    if (Math.abs(offsetDistance) < 0.1) {
      return midPoint;
    }

    // Trouver le point à la distance offsetDistance depuis le point médian
    let targetLength = midLength + offsetDistance;
    
    // Limiter la distance aux extrémités de la ligne
    targetLength = Math.max(0, Math.min(totalLength, targetLength));

    // Trouver le segment et la position sur ce segment
    currentLength = 0;
    for (let i = 0; i < segmentLengths.length; i++) {
      if (currentLength + segmentLengths[i] >= targetLength) {
        const segmentStart = points[i];
        const segmentEnd = points[i + 1];
        const segmentOffset = targetLength - currentLength;
        const segmentDx = segmentEnd.x - segmentStart.x;
        const segmentDy = segmentEnd.y - segmentStart.y;
        const segmentLength = segmentLengths[i];
        
        return {
          x: segmentStart.x + (segmentDx * (segmentOffset / segmentLength)),
          y: segmentStart.y + (segmentDy * (segmentOffset / segmentLength))
        };
      }
      currentLength += segmentLengths[i];
    }

    // Si on arrive ici, retourner le dernier point
    return points[points.length - 1];
  }

  /** Point situé à `fraction` de la longueur totale depuis la source (0–1). */
  private findPointFromStartFraction(
    points: { x: number; y: number }[],
    fraction: number
  ): { x: number; y: number } {
    if (points.length < 2) return points[0] || { x: 0, y: 0 };
    const f = Math.max(0, Math.min(1, fraction));
    let totalLength = 0;
    const segmentLengths: number[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      segmentLengths.push(len);
      totalLength += len;
    }
    if (totalLength < 0.01) return points[0];
    const target = f * totalLength;
    let acc = 0;
    for (let i = 0; i < segmentLengths.length; i++) {
      if (acc + segmentLengths[i] >= target) {
        const segLen = segmentLengths[i];
        const tSeg = (target - acc) / segLen;
        const p1 = points[i];
        const p2 = points[i + 1];
        return {
          x: p1.x + tSeg * (p2.x - p1.x),
          y: p1.y + tSeg * (p2.y - p1.y),
        };
      }
      acc += segmentLengths[i];
    }
    return points[points.length - 1];
  }

  private getPolylineLength(points: { x: number; y: number }[]): number {
    let totalLength = 0;
    for (let i = 0; i < points.length - 1; i++) {
      totalLength += Math.hypot(
        points[i + 1].x - points[i].x,
        points[i + 1].y - points[i].y
      );
    }
    return totalLength;
  }

  /** Distance le long du tracé depuis la source pour le badge sortie losange (fraction + marge mini). */
  private getDecisionExitBaseDistance(
    points: { x: number; y: number }[],
    fraction: number,
    minPxFromStart: number
  ): number {
    const totalLength = this.getPolylineLength(points);
    if (totalLength < 0.01) return 0;
    const f = Math.max(0, Math.min(1, fraction));
    const fromFraction = f * totalLength;
    return Math.min(
      totalLength * 0.998,
      Math.max(fromFraction, minPxFromStart)
    );
  }

  /** Point à la distance `distanceFromStart` (0 = source) le long de la polyligne. */
  private findPointAtDistanceFromStart(
    points: { x: number; y: number }[],
    distanceFromStart: number
  ): { x: number; y: number } {
    if (points.length < 2) return points[0] || { x: 0, y: 0 };
    let totalLength = 0;
    const segmentLengths: number[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      segmentLengths.push(len);
      totalLength += len;
    }
    if (totalLength < 0.01) return points[0];
    const targetDist = Math.max(0, Math.min(totalLength, distanceFromStart));
    let acc = 0;
    for (let i = 0; i < segmentLengths.length; i++) {
      if (acc + segmentLengths[i] >= targetDist) {
        const segLen = segmentLengths[i];
        const tSeg = (targetDist - acc) / segLen;
        const p1 = points[i];
        const p2 = points[i + 1];
        return {
          x: p1.x + tSeg * (p2.x - p1.x),
          y: p1.y + tSeg * (p2.y - p1.y),
        };
      }
      acc += segmentLengths[i];
    }
    return points[points.length - 1];
  }

  /**
   * Comme `findPointFromStartFraction`, mais garantit une distance mini depuis la source (losange),
   * pour placer le badge sur la flèche et non sur le sommet du losange.
   */
  private findPointFromStartWithClearance(
    points: { x: number; y: number }[],
    fraction: number,
    minPxFromStart: number
  ): { x: number; y: number } {
    const d = this.getDecisionExitBaseDistance(points, fraction, minPxFromStart);
    return this.findPointAtDistanceFromStart(points, d);
  }

  public renderLineText(
    line: Line,
    points: { x: number; y: number }[],
    visiblePath: d3.Selection<SVGPathElement, unknown, null, undefined>,
    fromBlock?: Block,
    editEnabled: boolean = this.editMode
  ) {
    // Remove existing text container if it exists
    this.container.selectAll(`.line-text-${line.id}`).remove();

    if (points.length < 2) return;

    let exitFraction: number | null = null;
    if (
      line.labelPositionFromStart !== undefined &&
      line.labelPositionFromStart !== null &&
      !Number.isNaN(Number(line.labelPositionFromStart))
    ) {
      exitFraction = Math.min(1, Math.max(0, Number(line.labelPositionFromStart)));
    } else if (fromBlock?.type === 'decision') {
      exitFraction = DECISION_EXIT_LABEL_FRACTION;
    }
    const labelNearSource = exitFraction != null;
    const isDecisionSource = fromBlock?.type === 'decision';
    const isDecisionExitPath = Boolean(labelNearSource && isDecisionSource);
    const exitDisplay = isDecisionExitPath ? getDecisionExitDisplayText(line) : '';
    const midLineText = !isDecisionExitPath ? (line.text || '').trim() : '';

    // Sortie de losange sans libellé : rien en lecture seule
    if (labelNearSource && !editEnabled) {
      if (isDecisionExitPath && !exitDisplay) return;
      if (!isDecisionExitPath && !midLineText) return;
    }

    // Direction pour drag (labels au milieu du tracé uniquement)
    let maxLength = 0;
    let bestSegmentStart = points[0];
    let bestSegmentEnd = points[1];

    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      if (length > maxLength) {
        maxLength = length;
        bestSegmentStart = p1;
        bestSegmentEnd = p2;
      }
    }

    let segmentDx = bestSegmentEnd.x - bestSegmentStart.x;
    let segmentDy = bestSegmentEnd.y - bestSegmentStart.y;
    let segmentLength = Math.sqrt(segmentDx * segmentDx + segmentDy * segmentDy);

    let unitX = 0;
    let unitY = 0;

    if (segmentLength > 0.1) {
      unitX = segmentDx / segmentLength;
      unitY = segmentDy / segmentLength;

      if (Math.abs(segmentDy) < Math.abs(segmentDx) * 0.1) {
        unitY = 0;
        unitX = segmentDx > 0 ? 1 : -1;
      } else if (Math.abs(segmentDx) < Math.abs(segmentDy) * 0.1) {
        unitX = 0;
        unitY = segmentDy > 0 ? 1 : -1;
      }
    } else {
      const firstPoint = points[0];
      const lastPoint = points[points.length - 1];
      const totalDx = lastPoint.x - firstPoint.x;
      const totalDy = lastPoint.y - firstPoint.y;
      const totalLength = Math.sqrt(totalDx * totalDx + totalDy * totalDy);
      if (totalLength > 0.1) {
        unitX = totalDx / totalLength;
        unitY = totalDy / totalLength;

        if (Math.abs(totalDy) < Math.abs(totalDx) * 0.1) {
          unitY = 0;
          unitX = totalDx > 0 ? 1 : -1;
        } else if (Math.abs(totalDx) < Math.abs(totalDy) * 0.1) {
          unitX = 0;
          unitY = totalDy > 0 ? 1 : -1;
        }
      } else {
        unitX = 1;
        unitY = 0;
      }
    }

    let textPosition: { x: number; y: number };
    if (labelNearSource && isDecisionExitPath) {
      const totalLen = this.getPolylineLength(points);
      const baseDist = this.getDecisionExitBaseDistance(
        points,
        exitFraction!,
        DECISION_EXIT_MIN_CLEARANCE_PX
      );
      const minD = DECISION_EXIT_MIN_CLEARANCE_PX;
      const maxD = totalLen * 0.998;
      const distFromStart = Math.max(
        minD,
        Math.min(maxD, baseDist + (line.exitLabelDragOffset || 0))
      );
      textPosition = this.findPointAtDistanceFromStart(points, distFromStart);
    } else if (labelNearSource) {
      textPosition = this.findPointFromStartFraction(points, exitFraction!);
    } else {
      textPosition = this.findPointAlongLine(points, line.textOffsetAlongLine || 0);
    }

    const midIndex = Math.floor((points.length - 1) / 2);
    const prevPoint = points[midIndex - 1];
    const nextPoint = points[midIndex + 1];

    const textContainer = this.container
      .append('div')
      .attr('class', `line-text line-text-${line.id}`)
      .style('position', 'absolute')
      .style('left', `${textPosition.x}px`)
      .style('top', `${textPosition.y}px`)
      .style('transform', 'translate(-50%, -50%)')
      .style('z-index', '2')
      .style('min-width', labelNearSource ? '24px' : '20px')
      .style('min-height', labelNearSource ? '18px' : '20px')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('pointer-events', editEnabled ? 'auto' : 'none')
      .style(
        'cursor',
        editEnabled && (!labelNearSource || isDecisionExitPath)
          ? 'grab'
          : editEnabled
            ? 'pointer'
            : 'default'
      );

    const dx = Math.abs((nextPoint?.x || 0) - (prevPoint?.x || 0));
    const dy = Math.abs((nextPoint?.y || 0) - (prevPoint?.y || 0));
    const isHorizontal = dx > dy;
    textContainer.style('transform', 'translate(-50%, -50%)');

    const applyExitBadge = (sel: d3.Selection<d3.BaseType, unknown, null, undefined>) => {
      sel
        .style('background-color', 'rgba(255, 255, 255, 0.92)')
        .style('box-shadow', '0 1px 2px rgba(15, 23, 42, 0.12)')
        .style('padding', '2px 7px')
        .style('border-radius', '6px')
        .style('font-size', '11px')
        .style('font-weight', '500')
        .style('color', STYLES.colors.text)
        .style('white-space', 'nowrap')
        .style('text-align', 'center')
        .style('user-select', 'none');
    };

    if (isDecisionExitPath) {
      if (exitDisplay) {
        applyExitBadge(textContainer.append('span').text(exitDisplay));
      }
    } else if (midLineText) {
      applyExitBadge(textContainer.append('span').text(midLineText));
    }

    if (editEnabled) {
      const dragState = { isDragging: false, hasDragged: false };

      textContainer.on('click', (event: MouseEvent) => {
        event.stopPropagation();

        if (dragState.hasDragged) {
          dragState.hasDragged = false;
          return;
        }

        this.startEditing(textContainer, line, points, isHorizontal, isDecisionExitPath);
      });

      if (!labelNearSource) {
        this.attachDragHandler(textContainer, line, points, { unitX, unitY }, dragState);
      } else if (isDecisionExitPath && exitFraction != null) {
        this.attachDecisionExitDragHandler(
          textContainer,
          line,
          points,
          exitFraction,
          { unitX, unitY },
          dragState
        );
      }
    }
  }

  private attachDecisionExitDragHandler(
    container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
    line: Line,
    points: { x: number; y: number }[],
    exitFraction: number,
    direction: { unitX: number; unitY: number },
    dragState: { isDragging: boolean; hasDragged: boolean }
  ) {
    const totalLength = this.getPolylineLength(points);
    const midLength = totalLength / 2;
    const baseDist = this.getDecisionExitBaseDistance(
      points,
      exitFraction,
      DECISION_EXIT_MIN_CLEARANCE_PX
    );
    const minDist = DECISION_EXIT_MIN_CLEARANCE_PX;
    const maxDist = totalLength * 0.998;

    let startClientX = 0;
    let startClientY = 0;
    let initialExitDragOffset = line.exitLabelDragOffset || 0;
    let containerRect: DOMRect | null = null;
    const DRAG_THRESHOLD = 5;

    const drag = d3.drag<HTMLDivElement, unknown>()
      .on('start', (event: d3.D3DragEvent<HTMLDivElement, unknown, unknown>) => {
        dragState.isDragging = false;
        dragState.hasDragged = false;
        startClientX = event.sourceEvent.clientX;
        startClientY = event.sourceEvent.clientY;
        const containerNode = this.container.node();
        if (containerNode) {
          containerRect = containerNode.getBoundingClientRect();
        }
        initialExitDragOffset = line.exitLabelDragOffset || 0;
        event.sourceEvent.stopPropagation();
      })
      .on('drag', (event: d3.D3DragEvent<HTMLDivElement, unknown, unknown>) => {
        if (!containerRect) return;
        const currentClientX = event.sourceEvent.clientX;
        const currentClientY = event.sourceEvent.clientY;
        const startX = startClientX - containerRect.left;
        const startY = startClientY - containerRect.top;
        const currentX = currentClientX - containerRect.left;
        const currentY = currentClientY - containerRect.top;
        const deltaX = currentX - startX;
        const deltaY = currentY - startY;
        const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (totalMovement > DRAG_THRESHOLD) {
          if (!dragState.isDragging) {
            dragState.isDragging = true;
            dragState.hasDragged = true;
            container.style('cursor', 'grabbing');
          }

          let tempDeltaAlongLine = deltaX * direction.unitX + deltaY * direction.unitY;
          const tempDistFromStart = Math.max(
            minDist,
            Math.min(maxDist, baseDist + initialExitDragOffset + tempDeltaAlongLine)
          );
          const tempOffsetFromMid = tempDistFromStart - midLength;
          const currentSegment = this.findCurrentSegment(points, tempOffsetFromMid);

          let deltaAlongLine = 0;
          if (currentSegment) {
            if (currentSegment.isHorizontal) {
              deltaAlongLine = deltaX * (currentSegment.unitX > 0 ? 1 : -1);
            } else if (currentSegment.isVertical) {
              deltaAlongLine = deltaY * (currentSegment.unitY > 0 ? 1 : -1);
            } else {
              deltaAlongLine =
                deltaX * currentSegment.unitX + deltaY * currentSegment.unitY;
            }
          } else {
            deltaAlongLine = deltaX * direction.unitX + deltaY * direction.unitY;
          }

          const newDistFromStart = Math.max(
            minDist,
            Math.min(maxDist, baseDist + initialExitDragOffset + deltaAlongLine)
          );
          line.exitLabelDragOffset = newDistFromStart - baseDist;

          const newPosition = this.findPointAtDistanceFromStart(points, newDistFromStart);
          container.style('left', `${newPosition.x}px`);
          container.style('top', `${newPosition.y}px`);
        }

        event.sourceEvent.stopPropagation();
      })
      .on('end', (event: d3.D3DragEvent<HTMLDivElement, unknown, unknown>) => {
        if (!containerRect) return;

        if (dragState.isDragging) {
          const finalDist = Math.max(
            minDist,
            Math.min(maxDist, baseDist + (line.exitLabelDragOffset || 0))
          );
          line.exitLabelDragOffset = finalDist - baseDist;
          const finalPosition = this.findPointAtDistanceFromStart(points, finalDist);
          container.style('left', `${finalPosition.x}px`);
          container.style('top', `${finalPosition.y}px`);
          this.onUpdate(line);
          setTimeout(() => {
            dragState.hasDragged = false;
          }, 100);
        } else {
          dragState.hasDragged = false;
        }

        dragState.isDragging = false;
        container.style('cursor', 'grab');
        containerRect = null;
        event.sourceEvent.stopPropagation();
      });

    container.call(drag);
  }

  private attachDragHandler(
    container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
    line: Line,
    points: { x: number; y: number }[],
    direction: { unitX: number; unitY: number },
    dragState: { isDragging: boolean; hasDragged: boolean }
  ) {
    let startClientX = 0;
    let startClientY = 0;
    let initialOffsetAlongLine = line.textOffsetAlongLine || 0;
    let containerRect: DOMRect | null = null;
    const DRAG_THRESHOLD = 5; // Seuil minimum de mouvement pour considérer un drag

    const drag = d3.drag<HTMLDivElement, unknown>()
      .on('start', (event: d3.D3DragEvent<HTMLDivElement, unknown, unknown>) => {
        dragState.isDragging = false;
        dragState.hasDragged = false;
        
        // Obtenir les coordonnées réelles de la souris
        startClientX = event.sourceEvent.clientX;
        startClientY = event.sourceEvent.clientY;
        
        // Obtenir la position du conteneur pour calculer les coordonnées relatives
        const containerNode = this.container.node();
        if (containerNode) {
          containerRect = containerNode.getBoundingClientRect();
        }
        
        initialOffsetAlongLine = line.textOffsetAlongLine || 0;
        event.sourceEvent.stopPropagation();
      })
      .on('drag', (event: d3.D3DragEvent<HTMLDivElement, unknown, unknown>) => {
        if (!containerRect) return;
        
        // Calculer le déplacement de la souris en coordonnées réelles
        const currentClientX = event.sourceEvent.clientX;
        const currentClientY = event.sourceEvent.clientY;
        
        // Convertir en coordonnées relatives au conteneur
        const startX = startClientX - containerRect.left;
        const startY = startClientY - containerRect.top;
        const currentX = currentClientX - containerRect.left;
        const currentY = currentClientY - containerRect.top;
        
        // Calculer le déplacement
        const deltaX = currentX - startX;
        const deltaY = currentY - startY;
        
        const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        
        // Si le mouvement dépasse le seuil, on considère que c'est un drag
        if (totalMovement > DRAG_THRESHOLD) {
          if (!dragState.isDragging) {
            dragState.isDragging = true;
            dragState.hasDragged = true;
            container.style('cursor', 'grabbing');
          }
          
          // Calculer un décalage temporaire pour trouver le segment actuel
          // On utilise une projection approximative pour déterminer le segment
          let tempDeltaAlongLine = deltaX * direction.unitX + deltaY * direction.unitY;
          const tempOffset = initialOffsetAlongLine + tempDeltaAlongLine;
          
          // Trouver le segment actuel où se trouve le label (avec le décalage temporaire)
          const currentSegment = this.findCurrentSegment(points, tempOffset);
          
          let deltaAlongLine = 0;
          
          if (currentSegment) {
            // Utiliser la direction du segment actuel (pas la direction globale)
            if (currentSegment.isHorizontal) {
              // Segment horizontal: utiliser le mouvement horizontal de la souris
              deltaAlongLine = deltaX * (currentSegment.unitX > 0 ? 1 : -1);
            } else if (currentSegment.isVertical) {
              // Segment vertical: utiliser le mouvement vertical de la souris
              deltaAlongLine = deltaY * (currentSegment.unitY > 0 ? 1 : -1);
            } else {
              // Segment diagonal: projeter le mouvement sur la direction du segment
              deltaAlongLine = deltaX * currentSegment.unitX + deltaY * currentSegment.unitY;
            }
          } else {
            // Fallback: utiliser la direction globale
            deltaAlongLine = deltaX * direction.unitX + deltaY * direction.unitY;
          }
          
          // Calculer le nouveau décalage le long de la ligne
          const newOffsetAlongLine = initialOffsetAlongLine + deltaAlongLine;
          
          // Sauvegarder immédiatement dans l'objet line pour éviter les retours en arrière
          line.textOffsetAlongLine = newOffsetAlongLine;
          
          // Trouver la position exacte le long de la ligne (pour rester sur la ligne)
          const newPosition = this.findPointAlongLine(points, newOffsetAlongLine);
          
          // Mettre à jour la position du label
          container.style('left', `${newPosition.x}px`);
          container.style('top', `${newPosition.y}px`);
        }
        
        event.sourceEvent.stopPropagation();
      })
      .on('end', (event: d3.D3DragEvent<HTMLDivElement, unknown, unknown>) => {
        if (!containerRect) return;
        
        if (dragState.isDragging) {
          // La position a déjà été sauvegardée dans line.textOffsetAlongLine pendant le drag
          // On s'assure juste que la position visuelle est correcte et on appelle onUpdate
          const finalOffset = line.textOffsetAlongLine || initialOffsetAlongLine;
          const finalPosition = this.findPointAlongLine(points, finalOffset);
          
          // Mettre à jour la position finale pour s'assurer qu'elle est correcte
          container.style('left', `${finalPosition.x}px`);
          container.style('top', `${finalPosition.y}px`);
          
          // Appeler onUpdate pour sauvegarder les changements
          this.onUpdate(line);
          
          // Garder hasDragged à true pendant un court moment pour éviter le clic
          setTimeout(() => {
            dragState.hasDragged = false;
          }, 100);
        } else {
          // Si pas de drag, réinitialiser immédiatement
          dragState.hasDragged = false;
        }
        
        dragState.isDragging = false;
        container.style('cursor', 'grab');
        containerRect = null;
        
        event.sourceEvent.stopPropagation();
      });

    container.call(drag);
  }

  private startEditing(
    container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
    line: Line,
    points: { x: number; y: number }[],
    isHorizontal: boolean,
    isDecisionExit: boolean
  ) {
    const currentTransform = container.style('transform');
    container.selectAll('*').remove();

    const initialValue = isDecisionExit ? getDecisionExitDisplayText(line) : line.text || '';

    const input = container
      .append('input')
      .attr('type', 'text')
      .attr('value', initialValue)
      .attr('maxlength', isDecisionExit ? 32 : 200)
      .style('width', isDecisionExit ? '88px' : '100px')
      .style('padding', '2px 6px')
      .style('border', '1px solid #e5e7eb')
      .style('border-radius', '6px')
      .style('outline', 'none')
      .style('font-size', '11px')
      .style('background', 'white')
      .style('text-align', 'center');

    const cleanup = () => {
      const raw = String(input.property('value') ?? '');
      input.remove();

      if (isDecisionExit) {
        line.exitLabel = clampDecisionExitLabel(raw).trim();
        line.text = '';
      } else {
        line.text = raw;
      }

      const display = isDecisionExit ? (line.exitLabel || '').trim() : (line.text || '').trim();
      if (display) {
        const span = container.append('span').text(display);
        span
          .style('background-color', 'rgba(255, 255, 255, 0.92)')
          .style('box-shadow', '0 1px 2px rgba(15, 23, 42, 0.12)')
          .style('padding', '2px 7px')
          .style('border-radius', '6px')
          .style('font-size', '11px')
          .style('font-weight', '500')
          .style('color', STYLES.colors.text)
          .style('white-space', 'nowrap')
          .style('text-align', 'center');
      }

      container.style('transform', currentTransform);
      this.onUpdate(line);

      document.removeEventListener('mousedown', handleOutsideClick);
    };

    const handleCleanup = (event?: KeyboardEvent) => {
      if (!event || event.key === 'Enter') {
        if (event) event.preventDefault();
        input.on('blur', null).on('keypress', null);
        cleanup();
      }
    };

    // Fonction pour détecter les clics en dehors de l'input
    const handleOutsideClick = (event: MouseEvent) => {
      if (input.node() && !input.node()?.contains(event.target as Node)) {
        cleanup();
      }
    };

    // Écouteur de clic global
    document.addEventListener('mousedown', handleOutsideClick);

    input.on('blur', handleCleanup).on('keypress', handleCleanup);

    requestAnimationFrame(() => {
      const inputNode = input.node();
      if (inputNode) {
        inputNode.focus();
        inputNode.select();
      }
    });
  }

}